import React, { useState, useEffect } from 'react';
import './App.scss';

import { HashRouter as Router, Switch, Route, Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { History } from 'history'

import { Button, AnchorButton, Navbar, Alignment, Popover, Menu, MenuItem, Position, Spinner, Tooltip } from "@blueprintjs/core";

import Login, { LoginCredential } from './Login';
import AccountMain from './Account';
import UserMain from './User';
import CurrencyMain from './Currency';
import CustomerMain from './Customer';
import VendorMain from './Vendor';
import NumberMain from './Number';
import EnvironmentMain from './Environment';
import EasySetupMain from './EasySetup';
import ImportMain from './Import';
import DataMaintenanceMain from './DataMaintenance';
import TrialBalanceMain from './TrialBalance';
import BalanceSheetMain from './BalanceSheet';
import IncomeStatementMain from './IncomeStatement';
import IncomeStatementCompMain from './IncomeStatementComp';
import CashFlowReportMain from './CashFlowReport';
import FRRMain from './FRR';
import ActivityReportMain from './ActivityReport';
import JournalListingMain from './JournalListing';
import ReceivableAgingReportMain from './ReceivableAgingReport';
import PayableAgingReportMain from './PayableAgingReport';
import VoucherMain from './Voucher';
import ReceivableMain from './Receivable';
import PayableMain from './Payable';
import ReceiptMain from './Receipt';
import PaymentMain from './Payment';
import TaskMain from './Task';
import CreateEmailMain from './CreateEmail';
import ConfirmEmailMain from './ConfirmEmail';
import RegisterMain from './Register';
import SysInfoMain from './SysInfo';
import * as utils from "./utils";

import md5 from './md5'

import { Environment, LicenseStatus } from './Types'

import axios from 'axios';

import svgLogo from './svg/logo.svg'
import svgAccount from './svg/coa.svg'
import svgFolder from './svg/folder.svg'
import svgEasySetup from './svg/easysetup.svg'
import svgImport from './svg/import.svg'
import svgDownload from './svg/download.svg'
import svgDatabase from './svg/database.svg'
import svgReport from './svg/report.svg'
import svgPaper from './svg/paper.svg'
import svgUser from './svg/user.svg'
import svgCustomer from './svg/customer.svg'
import svgVendor from './svg/vendor.svg'
import svgCurrency from './svg/currency.svg'
import svgNumber from './svg/number.svg'
import svgTransaction from './svg/transaction.svg'
import svgVoucher from './svg/voucher.svg'
import svgTask from './svg/task.svg'
import svgTools from './svg/tools.svg'
import svgSort from './svg/sort.svg'
import svgExclamation from './svg/exclamation.svg'
import svgWarning from './svg/warning.svg'
import svgRegister from './svg/register.svg'
import svgMoneyIn from './svg/money-in.svg'
import svgMoneyOut from './svg/money-out.svg'

import i18next from 'i18next';

// export const brandingText = "AC3x" // "ActiveBooks"
// export const productName = "POP AC3x" // "ActiveBooks Accounting"
export const versionString = "1.0.20"

declare var brandingText: string;
declare var productName: string;
declare var isAC3x: boolean;

document.title = productName;

const svc = axios.create({
    baseURL: process.env.NODE_ENV === "development" ? 'http://localhost:8818/api' : '/api',
})
const hostId = process.env.NODE_ENV === "development" ? md5('localhost:8818').slice(-8) : md5(window.location.host).slice(-8)

const reg = axios.create({
    baseURL: process.env.NODE_ENV === "development" ? 'http://localhost:8810/api' : 'https://reg.activebooks.com.hk/api',
})

/*
// try load config from server, can be a real file or a config in web server
axios.get("config.json").then((res) => {
    //console.log(res.data)
    svc.defaults.baseURL = res.data.baseUrl
}).catch(() => {
    // do nothing, use default
})
*/

function App() {
    const [isLoggedIn, setIsLoggedIn] = useState(false) // useState(initIsLoggedIn)
    const [currentUser, setCurrentUser] = useState("")
    const [currentEnv, setCurrentEnv] = useState({} as Environment)
    const [envList, setEnvList] = useState([] as Environment[])
    const [licenseStatus, setLicenseStatus] = useState({} as LicenseStatus)

    const pingServer = () => {
        (async () => {
            try {
                await svc.post('/ping',
                    {});
                //console.log(res.data)
            } catch (error) {
                //utils.showError(error)
                //console.log(error)
            }
        })()
    }

    /* setInterval not realiable e.g. when browser is minimized
    useEffect(() => {
        pingServer()
        const interval = setInterval(pingServer, 5000)
        return () => clearInterval(interval)
    }, [])
    */

    useEffect(() => {
        pingServer()
        const worker = new Worker('worker.js')
        worker.onmessage = () => {
            pingServer()
        }
        return () => worker.terminate()
    }, [])

    window.onkeydown = (e: KeyboardEvent) => {
        //console.log(`ctrl:${e.ctrlKey} alt:${e.altKey} shift:${e.shiftKey} meta:${e.metaKey} key:${e.key} keyCode:${e.keyCode}`)

        // disable ctrl + s to prevent save page when user trying to submit form while lost focus
        if (e.ctrlKey && e.keyCode === 83) {
            e.preventDefault()
        }
    }

    const handleLogin = (credential: LoginCredential, history: History) => {
        (async () => {
            try {
                // usually A/E code is prefixed with C, but some user may use others e.g. Upbest uses A for A/E
                // TODO: Read A/E prefix from config
                const res = await svc.post('/login',
                    {
                        userId: credential.login,
                        password: credential.password,
                    });
                if (res.data.ok) {
                    //console.log('Bearer '+res.data.token)
                    //console.log(res.data)
                    /*
                    svc.defaults.headers = {
                        'Authorization': 'Bearer ' + res.data.token
                    }
                    */
                    svc.defaults.headers["Authorization"] = 'Bearer ' + res.data.token
                    localStorage.setItem('token', res.data.token)
                    document.cookie = `token_${hostId}=${res.data.token}`
                    setIsLoggedIn(true)
                    history.push("/")
                } else {
                    //console.log(res.data)
                    utils.showError(res.data.message)
                }
            } catch (error) {
                utils.showError(error)
            }
        })()
    }

    const handleLogout = () => {
        svc.defaults.headers = {}
        localStorage.removeItem('token')
        document.cookie = `token_${hostId}=; expires=Thu, 01 Jan 1970 00:00:00 UTC;`
        document.cookie = `env_${hostId}=; expires=Thu, 01 Jan 1970 00:00:00 UTC;`
        setIsLoggedIn(false)
        setCurrentUser("")
        setCurrentEnv({} as Environment)
    }

    const handleChangeEnv = (env: Environment, history: History) => {
        setCurrentEnv(env)
        history.push("/")
    }

    const handleRegister = () => {
        refreshLicenseStatus()
    }

    /*
    const token = localStorage.getItem('token')
    let initIsLoggedIn = false
    if (token) {
        svc.defaults.headers = {
            'Authorization': 'Bearer ' + token
        }
        document.cookie = `token=${token}`
        initIsLoggedIn = true
    }
    */

    useEffect(() => {
        console.log("useEffect []");
        const token = localStorage.getItem('token')
        if (token) {
            /*
            svc.defaults.headers = {
                'Authorization': 'Bearer ' + token
            }
            */
            svc.defaults.headers["Authorization"] = 'Bearer ' + token
            document.cookie = `token_${hostId}=${token}`
            setIsLoggedIn(true);
        }
    }, [])

    useEffect(() => {
        console.log("useEffect [isLoggedIn]", isLoggedIn);
        (async () => {
            try {
                if (isLoggedIn) {
                    const res = await svc.post('/usergetcurrent');
                    //console.log(res.data)
                    if (res.data.ok) {
                        svc.defaults.headers["AC3X_ENV"] = res.data.env.envId;
                        document.cookie = `env_${hostId}=${res.data.env.envId}`;
                        setCurrentUser(res.data.data.userId)
                        setCurrentEnv(res.data.env)
                        localStorage.setItem("lang", res.data.data.currentLang)
                        i18next.changeLanguage(res.data.data.currentLang)
                    }
                }
            } catch (error) {
                utils.showError(error)
            }
            refreshLicenseStatus()
            /*
            try {
                if (isLoggedIn) {
                    const res = await svc.post('/licensestatus');
                    //console.log(res.data)
                    if (res.data.ok) {
                        setLicenseStatus(res.data.data)
                    }
                }
            } catch (error) {
                utils.showError(error)
            }
            */
        })()
    }, [isLoggedIn])

    const refreshEnvironments = () => {
        (async () => {
            try {
                const res = await svc.post('/environmentlist');
                setEnvList(res.data.data)
            } catch (error) {
                utils.showError(error)
            }
        })()
    }

    const refreshLicenseStatus = () => {
        (async () => {
            try {
                const res = await svc.post('/licensestatus');
                setLicenseStatus(res.data.data)
            } catch (error) {
                utils.showError(error)
            }
        })()
    }

    useEffect(() => {
        console.log("useEffect [envList]");
        /*
        if ((envList.length > 0) && ("envId" in currentEnv) && (envList.findIndex((e) => e.envId === currentEnv.envId) === -1)) {
            utils.showError("Environment changed")
            setCurrentEnv(envList[0])
        }
        */
        if ((envList.length > 0) && ("envId" in currentEnv)) {
            let idx = envList.findIndex((e) => e.envId === currentEnv.envId)
            if (idx === -1) {
                utils.showError("Environment changed")
                setCurrentEnv(envList[0])
            } else {
                setCurrentEnv(envList[idx])
            }
        }
    }, [envList, currentEnv])

    useEffect(() => {
        console.log("useEffect []");
        /*
        (async () => {
            try {
                {
                    const res = await svc.post('/environmentlist');
                    setEnvList(res.data.data)
                }
            } catch (error) {
                utils.showError(error)
            }
        })()
        */
       refreshEnvironments()
    }, [])

    useEffect(() => {
        console.log("useEffect [currentEnv]");
        (async () => {
            try {
                if ("envId" in currentEnv) {
                    //console.log("ac3x_env=", currentEnv.envId)
                    svc.defaults.headers["AC3X_ENV"] = currentEnv.envId;
                    document.cookie = `env_${hostId}=${currentEnv.envId}`;
                    await svc.post('/userupdatecurrent', {currentEnv: currentEnv.envId});
                }
            } catch (error) {
                utils.showError(error)
            }
        })()
    }, [currentEnv])

    return (
        <React.Suspense fallback={<Spinner></Spinner>}>
        <div className="App">
            <Router>
                <MenuBar isLoggedIn={isLoggedIn} userId={currentUser} envList={envList} currentEnv={currentEnv} onChangeEnv={handleChangeEnv} onRefreshEnv={refreshEnvironments} licenseStatus={licenseStatus} />
                <div className="AppBody">
                    <Switch>
                        <Route exact path="/">
                            {isLoggedIn ? <MainMenu currentEnv={currentEnv} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        <Route exact path="/login">
                            <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />
                        </Route>
                        <Route path="/accounts/:tabId?">
                            {isLoggedIn ? <AccountMain svc={svc} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        <Route path="/users/:tabId?">
                            {isLoggedIn ? <UserMain svc={svc} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        <Route path="/currencies/:tabId?">
                            {isLoggedIn ? <CurrencyMain svc={svc} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        <Route path="/customers/:tabId?">
                            {isLoggedIn ? <CustomerMain svc={svc} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        <Route path="/vendors/:tabId?">
                            {isLoggedIn ? <VendorMain svc={svc} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        <Route path="/numbers/:tabId?">
                            {isLoggedIn ? <NumberMain svc={svc} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        <Route path="/environments/:tabId?">
                            {isLoggedIn ? <EnvironmentMain svc={svc} onUpdate={refreshEnvironments} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        <Route path="/easysetup">
                            {isLoggedIn ? <EasySetupMain svc={svc} currentEnv={currentEnv} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        <Route path="/vouchers/:tabId?/:journalCode?/:voucherCode?">
                            {isLoggedIn ? <VoucherMain svc={svc} currentEnv={currentEnv} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        <Route path="/receivables/:tabId?/:journalCode?/:voucherCode?">
                            {isLoggedIn ? <ReceivableMain svc={svc} currentEnv={currentEnv} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        <Route path="/payables/:tabId?/:journalCode?/:voucherCode?">
                            {isLoggedIn ? <PayableMain svc={svc} currentEnv={currentEnv} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        <Route path="/receipts/:tabId?/:journalCode?/:voucherCode?">
                            {isLoggedIn ? <ReceiptMain svc={svc} currentEnv={currentEnv} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        <Route path="/payments/:tabId?/:journalCode?/:voucherCode?">
                            {isLoggedIn ? <PaymentMain svc={svc} currentEnv={currentEnv} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        <Route path="/import">
                            {isLoggedIn ? <ImportMain svc={svc} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        <Route path="/datamaintenance">
                            {isLoggedIn ? <DataMaintenanceMain svc={svc} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        <Route exact path="/trialbalance">
                            {isLoggedIn ? <TrialBalanceMain svc={svc} currentEnv={currentEnv} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        <Route exact path="/balancesheet">
                            {isLoggedIn ? <BalanceSheetMain svc={svc} currentEnv={currentEnv} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        <Route exact path="/incomestatement">
                            {isLoggedIn ? <IncomeStatementMain svc={svc} currentEnv={currentEnv} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        {!currentEnv.multiCurrency && <Route exact path="/incomestatementcomp">
                            {isLoggedIn ? <IncomeStatementCompMain svc={svc} currentEnv={currentEnv} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>}
                        {/*
                        <Route exact path="/cashflowreport">
                            {isLoggedIn ? <CashFlowReportMain svc={svc} currentEnv={currentEnv} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        */}
                        {isAC3x && <Route exact path="/frr">
                            {isLoggedIn ? <FRRMain svc={svc} currentEnv={currentEnv} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>}
                        <Route exact path="/activityreport">
                            {isLoggedIn ? <ActivityReportMain svc={svc} currentEnv={currentEnv} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        <Route exact path="/journallisting">
                            {isLoggedIn ? <JournalListingMain svc={svc} currentEnv={currentEnv} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        <Route exact path="/receivableagingreport">
                            {isLoggedIn ? <ReceivableAgingReportMain svc={svc} currentEnv={currentEnv} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        <Route exact path="/payableagingreport">
                            {isLoggedIn ? <PayableAgingReportMain svc={svc} currentEnv={currentEnv} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        <Route path="/tasks">
                            {isLoggedIn ? <TaskMain svc={svc} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        <Route path="/createemail">
                            {isLoggedIn ? <CreateEmailMain reg={reg} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        <Route path="/confirmemail">
                            {isLoggedIn ? <ConfirmEmailMain reg={reg} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        <Route path="/register">
                            {isLoggedIn ? <RegisterMain reg={reg} svc={svc} licenseStatus={licenseStatus} onRegister={handleRegister} /> : <Login isLoggedIn={isLoggedIn} onLogin={handleLogin} onLogout={handleLogout} />}
                        </Route>
                        <Route path="/sysinfo">
                            <SysInfoMain svc={svc} />
                        </Route>
                    </Switch>
                </div>
            </Router>
        </div>
        </React.Suspense>
    );
}

interface MainMenuProps {
    currentEnv?: Environment
}

function MainMenu({ currentEnv = {} as Environment}: MainMenuProps) {
    const { t, i18n } = useTranslation();
    return (
        <>
            <div className="Row">
                <Link to="/vouchers">
                    <div className="MenuTile">
                        <img className="MenuTileImage" src={svgVoucher} alt="Vouchers" />
                        <div>{t('vouchers')}</div>
                    </div>
                </Link>
                <Link to="/receivables">
                    <div className="MenuTile">
                        <img className="MenuTileImage" src={svgVoucher} alt="Receivables" />
                        <div>{t('receivables')}</div>
                    </div>
                </Link>
                <Link to="/payables">
                    <div className="MenuTile">
                        <img className="MenuTileImage" src={svgVoucher} alt="Payables" />
                        <div>{t('payables')}</div>
                    </div>
                </Link>
                <Link to="/receipts">
                    <div className="MenuTile">
                        <img className="MenuTileImage" src={svgMoneyIn} alt="Receipts" />
                        <div>{t('receipts')}</div>
                    </div>
                </Link>
                <Link to="/payments">
                    <div className="MenuTile">
                        <img className="MenuTileImage" src={svgMoneyOut} alt="Payments" />
                        <div>{t('payments')}</div>
                    </div>
                </Link>
            </div>

            <div className="Spacing-V-16"></div>
            <div className="Row">
                <Link to="/trialbalance">
                    <div className="MenuTile">
                        <img className="MenuTileImage" src={svgPaper} alt="Trial Balance" />
                        <div>{t('trial_balance')}</div>
                    </div>
                </Link>
                <Link to="/balancesheet">
                    <div className="MenuTile">
                        <img className="MenuTileImage" src={svgPaper} alt="Balance Sheet" />
                        <div>{t('balance_sheet')}</div>
                    </div>
                </Link>
                <Link to="/incomestatement">
                    <div className="MenuTile">
                        <img className="MenuTileImage" src={svgPaper} alt="Income Statement" />
                        <div>{t('income_statement')}</div>
                    </div>
                </Link>
                {!currentEnv.multiCurrency && <Link to="/incomestatementcomp">
                    <div className="MenuTile">
                        <img className="MenuTileImage" src={svgPaper} alt="Income Statement (Comparative)" />
                        <div>{t('income_statement_comparative')}</div>
                    </div>
                </Link>}
                {/*
                <Link to="/cashflowreport">
                    <div className="MenuTile">
                        <img className="MenuTileImage" src={svgPaper} alt="Cash Flow Report" />
                        <div>{t('cash_flow_report')}</div>
                    </div>
                </Link>
                */}
                {isAC3x && <Link to="/frr">
                    <div className="MenuTile">
                        <img className="MenuTileImage" src={svgDownload} alt="FRR" />
                        <div>{t('frr_download')}</div>
                    </div>
                </Link>}
            </div>

            <div className="Spacing-V-16"></div>
            <div className="Row">
                <Link to="/activityreport">
                    <div className="MenuTile">
                        <img className="MenuTileImage" src={svgPaper} alt="Activity Report" />
                        <div>{t('activity_report')}</div>
                    </div>
                </Link>
                <Link to="/journallisting">
                    <div className="MenuTile">
                        <img className="MenuTileImage" src={svgPaper} alt="Journal Listing" />
                        <div>{t('journal_listing')}</div>
                    </div>
                </Link>
                <Link to="/receivableagingreport">
                    <div className="MenuTile">
                        <img className="MenuTileImage" src={svgPaper} alt="Receivable Aging Report" />
                        <div>{t('receivable_aging_report')}</div>
                    </div>
                </Link>
                <Link to="/payableagingreport">
                    <div className="MenuTile">
                        <img className="MenuTileImage" src={svgPaper} alt="Payable Aging Report" />
                        <div>{t('payable_aging_report')}</div>
                    </div>
                </Link>
            </div>

            <div className="Spacing-V-16"></div>
            <div className="Row">
                <Link to="/accounts">
                    <div className="MenuTile">
                        <img className="MenuTileImage" src={svgAccount} alt="Chart of Account" />
                        <div>{t('chart_of_accounts')}</div>
                    </div>
                </Link>
            </div>
        </>
    )
}

interface MenuBarProps {
    isLoggedIn?: boolean
    userId?: string
    envList?: Environment[]
    currentEnv?: Environment
    onChangeEnv?: (env: Environment, history: History) => void
    onRefreshEnv?: () => void
    licenseStatus?: LicenseStatus
}

function MenuBar({ isLoggedIn = false, userId = "", envList = [], currentEnv = {} as Environment, onChangeEnv = () => {}, onRefreshEnv = () => {}, licenseStatus = {} as LicenseStatus }: MenuBarProps) {
    const { t, i18n } = useTranslation();
    const history = useHistory()

    const imgLogo = <img className="AppLogo" src={svgLogo} alt="Logo" />
    const imgAccount = <img className="MenuIcon" src={svgAccount} alt="Account" />
    const imgDatabase = <img className="MenuIcon" src={svgDatabase} alt="Database" />
    const imgTransaction = <img className="MenuIcon" src={svgTransaction} alt="Transaction" />
    const imgVoucher = <img className="MenuIcon" src={svgVoucher} alt="Voucher" />
    const imgMoneyIn = <img className="MenuIcon" src={svgMoneyIn} alt="Money In" />
    const imgMoneyOut = <img className="MenuIcon" src={svgMoneyOut} alt="Money Out" />
    const imgTools = <img className="MenuIcon" src={svgTools} alt="Tools" />
    const imgSort = <img className="MenuIcon" src={svgSort} alt="Sort" />
    const imgNumber = <img className="MenuIcon" src={svgNumber} alt="Number" />
    const imgImport = <img className="MenuIcon" src={svgImport} alt="Import" />
    const imgReport = <img className="MenuIcon" src={svgReport} alt="Report" />
    const imgPaper = <img className="MenuIcon" src={svgPaper} alt="Report" />
    const imgUser = <img className="MenuIcon" src={svgUser} alt="Login" />
    const imgCustomer = <img className="MenuIcon" src={svgCustomer} alt="Customer" />
    const imgVendor = <img className="MenuIcon" src={svgVendor} alt="Vendor" />
    const imgCurrency = <img className="MenuIcon" src={svgCurrency} alt="Customer" />
    const imgTask = <img className="MenuIcon" src={svgTask} alt="Task" />
    const imgEnvironment = <img className="MenuIcon" src={svgFolder} alt="Environment" />
    const imgEasySetup = <img className="MenuIcon" src={svgEasySetup} alt="Easy Setup" />
    const imgExclamation = <img className="MenuIcon" src={svgExclamation} alt="Exclamation" />
    const imgWarning = <img className="MenuIcon" src={svgWarning} alt="Warning" />
    const imgRegister = <img className="MenuIcon" src={svgRegister} alt="Register" />

    const menuDatabase =
        <Popover minimal position={Position.BOTTOM_LEFT} content={
            <Menu>
                <MenuItem icon={imgAccount} text={t('chart_of_accounts')} href="#/accounts" />
                {currentEnv.multiCurrency && <MenuItem icon={imgCurrency} text={t('currencies')} href="#/currencies" />}
                <MenuItem icon={imgCustomer} text={t('customers')} href="#/customers" />
                <MenuItem icon={imgVendor} text={t('vendors')} href="#/vendors" />
                <MenuItem icon={imgEasySetup} text={t('easy_setup')} href="#/easysetup" />
                <Menu.Divider />
                <MenuItem icon={imgUser} text={t('users')} href="#/users" />
                <MenuItem icon={imgEnvironment} text={t('environments')} href="#/environments" />
            </Menu>
        }>
            <Button minimal icon={imgDatabase} text={t('database')} />
        </Popover>
    const menuTransaction =
    <Popover minimal position={Position.BOTTOM_LEFT} content={
        <Menu>
                <MenuItem icon={imgVoucher} text={t('vouchers')} href="#/vouchers" />
                <MenuItem icon={imgVoucher} text={t('receivables')} href="#/receivables" />
                <MenuItem icon={imgVoucher} text={t('payables')} href="#/payables" />
                <MenuItem icon={imgMoneyIn} text={t('receipts')} href="#/receipts" />
                <MenuItem icon={imgMoneyOut} text={t('payments')} href="#/payments" />
        </Menu>
    }>
        <Button minimal icon={imgTransaction} text={t('transaction')} />
    </Popover>
    const menuTools =
    <Popover minimal position={Position.BOTTOM_LEFT} content={
        <Menu>
            <MenuItem icon={imgSort} text={t('data_file_maintenance')} href="#/datamaintenance" />
            <MenuItem icon={imgNumber} text={t('numbers')} href="#/numbers" />
            <MenuItem icon={imgImport} text={t('import_files')} href="#/import" />
            <MenuItem icon={imgRegister} text={t('register')} href="#/register" />
        </Menu>
    }>
        <Button minimal icon={imgTools} text={t('tools')} />
    </Popover>
    const menuReport =
    <Popover minimal position={Position.BOTTOM_LEFT} content={
        <Menu>
            <MenuItem icon={imgPaper} text={t('trial_balance')} href="#/trialbalance" />
            <MenuItem icon={imgPaper} text={t('balance_sheet')} href="#/balancesheet" />
            <MenuItem icon={imgPaper} text={t('income_statement')} href="#/incomestatement" />
            {!currentEnv.multiCurrency && <MenuItem icon={imgPaper} text={t('income_statement_comparative')} href="#/incomestatementcomp" />}
            {/*<MenuItem icon={imgPaper} text={t('cash_flow_report')} href="#/cashflowreport" />*/}
            <Menu.Divider />
            <MenuItem icon={imgPaper} text={t('activity_report')} href="#/activityreport" />
            <MenuItem icon={imgPaper} text={t('journal_listing')} href="#/journallisting" />
            <MenuItem icon={imgPaper} text={t('receivable_aging_report')} href="#/receivableagingreport" />
            <MenuItem icon={imgPaper} text={t('payable_aging_report')} href="#/payableagingreport" />
        </Menu>
    }>
        <Button minimal icon={imgReport} text={t('report')} />
    </Popover>

    const changeUserLanguage = (lng: string) => {
        (async () => {
            try {
                if (isLoggedIn) {
                    await svc.post('/userupdatecurrent', {currentLang: lng});
                }
            } catch (error) {
                utils.showError(error)
            }
        })()
    }

    let lngList = [
        {lngName: "English", lng: "en"},
        {lngName: "繁體中文", lng: "zh-HK"},
        {lngName: "简体中文", lng: "zh-CN"},
    ]

    return (
        <>
            <Navbar className="No-Print">
                <Navbar.Group align={Alignment.LEFT}>
                    <Navbar.Heading><AnchorButton minimal icon={imgLogo} text={brandingText} href="#/" onClick={onRefreshEnv} /></Navbar.Heading>
                    <Navbar.Divider />
                    {isLoggedIn ? menuDatabase : null}
                    {isLoggedIn ? menuTransaction : null}
                    {isLoggedIn ? menuReport : null}
                    {isLoggedIn ? menuTools : null}
                </Navbar.Group>
                <Navbar.Group align={Alignment.RIGHT}>
                    {isLoggedIn && <div className="No-SmallScreen">{currentEnv.companyName}</div>}
                    {isLoggedIn && <Navbar.Divider />}
                    {isLoggedIn && <Popover minimal position={Position.BOTTOM_LEFT} content={
                        <Menu>
                            {envList.map((env) => <MenuItem key={env.envId} icon={imgEnvironment} text={env.envName} onClick={() => {onChangeEnv(env, history)}} />)}
                        </Menu>
                    }>
                        <Button className="MenuEnv" minimal icon={imgEnvironment} text={currentEnv.envName} />
                    </Popover>}
                    {isLoggedIn && <Navbar.Divider />}
                    {isLoggedIn && <Link to="/tasks"><Button minimal icon={imgTask} /></Link>}
                    {isLoggedIn && <Link to="/login"><Button minimal icon={imgUser} text={userId} /></Link>}
                    <Popover minimal position={Position.BOTTOM_LEFT} content={
                        <Menu>
                            {lngList.map((lng) => <MenuItem key={lng.lng} icon="translate" text={lng.lngName} onClick={() => {i18n.changeLanguage(lng.lng); changeUserLanguage(lng.lng)}} />)}
                        </Menu>
                    }>
                        <Button className="MenuEnv" minimal icon="translate" text="Language" />
                    </Popover>
                    {!licenseStatus.hasLicense && <Link to="/register"><Tooltip content={t('not_registered')}><Button minimal icon={imgExclamation} /></Tooltip></Link>}
                    {licenseStatus.hasLicense && !licenseStatus.isValidLicense && <Link to="/register"><Tooltip content={t('invalid_license')}><Button minimal icon={imgWarning} /></Tooltip></Link>}
                    {licenseStatus.hasLicense && licenseStatus.isValidLicense && !licenseStatus.isValidMachine && <Link to="/register"><Tooltip content={t('invalid_machine')}><Button minimal icon={imgWarning} /></Tooltip></Link>}
                </Navbar.Group>
            </Navbar>
            <div className="Spacing-V-16"></div>
        </>
    )
}

export default App;
